import type { WorldLayout } from '../game/worldLayout';
import type {
  AnnotationCategory,
  AnnotationDoorTarget,
  AnnotationFunction,
  AnnotationShape,
  AnnotationSize,
  AnnotationTilePoint,
  WorldAnnotation,
} from '../types/worldAnnotation';
import {
  normalizeAnnotationInteractions,
  normalizeAnnotationStateRecord,
  normalizeAnnotationTriggers,
} from './worldAnnotationRuntime';

export const DEFAULT_INTERACTION_RADIUS = 2;

const VALID_CATEGORIES = new Set<AnnotationCategory>([
  'furniture',
  'area',
  'sign',
  'door',
  'device',
  'nature',
  'container',
  'landmark',
  'custom',
]);
const VALID_FUNCTIONS = new Set<AnnotationFunction>([
  'meeting',
  'research',
  'work',
  'rest',
  'social',
  'storage',
  'display',
  'production',
  'resource_node',
]);
const VALID_SHAPES = new Set<AnnotationShape>(['point', 'rect', 'circle']);

export const toFiniteNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const clampTile = (value: unknown, maximum: number): number =>
  Math.max(0, Math.min(Math.max(0, maximum), Math.floor(toFiniteNumber(value, 0))));

export const sanitizeString = (value: unknown, fallback = ''): string =>
  (typeof value === 'string' ? value : fallback).trim();

export const preserveString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

export const normalizeNameToken = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, ' ');

const NAME_MATCH_IGNORED_TOKENS = new Set([
  'a',
  'an',
  'the',
  'to',
  'go',
  'move',
  'walk',
  'travel',
  'head',
  'toward',
  'towards',
  'into',
  'onto',
  'at',
]);

export const tokenizeName = (value: string): string[] =>
  normalizeNameToken(value)
    .split(' ')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0 && !NAME_MATCH_IGNORED_TOKENS.has(entry));

const normalizeTags = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const tags = value
    .map((entry) => String(entry).trim().toLowerCase())
    .filter((entry, index, list) => entry.length > 0 && list.indexOf(entry) === index)
    .slice(0, 12);
  return tags.length > 0 ? tags : undefined;
};

export const normalizeCategory = (value: unknown): AnnotationCategory =>
  VALID_CATEGORIES.has(value as AnnotationCategory) ? (value as AnnotationCategory) : 'custom';

export const normalizeShape = (value: unknown): AnnotationShape =>
  VALID_SHAPES.has(value as AnnotationShape) ? (value as AnnotationShape) : 'point';

export const normalizeFunction = (value: unknown): AnnotationFunction | undefined =>
  VALID_FUNCTIONS.has(value as AnnotationFunction) ? (value as AnnotationFunction) : undefined;

export const normalizeCapacity = (value: unknown): number | undefined => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  const normalized = Math.floor(parsed);
  return normalized > 0 ? normalized : undefined;
};

const normalizeStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const entries = value
    .map((entry) => String(entry ?? '').trim())
    .filter((entry, index, list) => entry.length > 0 && list.indexOf(entry) === index);

  return entries.length > 0 ? entries : undefined;
};

const normalizeSeasonalMultiplier = (value: unknown): Record<string, number> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const entries = Object.entries(value)
    .map(([seasonId, multiplier]) => {
      const normalizedSeasonId = seasonId.trim();
      const normalizedMultiplier = Number(multiplier);
      if (!normalizedSeasonId || !Number.isFinite(normalizedMultiplier)) {
        return null;
      }
      return [normalizedSeasonId, normalizedMultiplier] as const;
    })
    .filter((entry): entry is readonly [string, number] => entry !== null);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

export const normalizeProductionConfig = (
  value: unknown,
): WorldAnnotation['productionConfig'] | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as Record<string, unknown>;
  const availableRecipes = normalizeStringArray(candidate.availableRecipes);
  const productionBonus = Number(candidate.productionBonus);
  const capacity = normalizeCapacity(candidate.capacity);

  if (!availableRecipes && !Number.isFinite(productionBonus) && capacity == null) {
    return undefined;
  }

  return {
    ...(availableRecipes ? { availableRecipes } : {}),
    ...(Number.isFinite(productionBonus) ? { productionBonus: Math.max(0, productionBonus) } : {}),
    ...(capacity != null ? { capacity } : {}),
  };
};

export const normalizeResourceNodeConfig = (
  value: unknown,
): WorldAnnotation['resourceNodeConfig'] | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as Record<string, unknown>;
  const resourceType = sanitizeString(candidate.resourceType);
  const yieldPerGather = Number(candidate.yieldPerGather);
  if (!resourceType || !Number.isFinite(yieldPerGather) || yieldPerGather <= 0) {
    return undefined;
  }

  const maxYield = Number(candidate.maxYield);
  const currentYield = Number(candidate.currentYield);
  const regenerationRate = Number(candidate.regenerationRate);
  const requiredTool = sanitizeString(candidate.requiredTool);
  const seasonalMultiplier = normalizeSeasonalMultiplier(candidate.seasonalMultiplier);

  return {
    resourceType,
    yieldPerGather: Math.max(1, Math.floor(yieldPerGather)),
    ...(Number.isFinite(maxYield) ? { maxYield: Math.max(0, Math.floor(maxYield)) } : {}),
    ...(Number.isFinite(currentYield)
      ? { currentYield: Math.max(0, Math.floor(currentYield)) }
      : {}),
    ...(Number.isFinite(regenerationRate)
      ? { regenerationRate: Math.max(0, regenerationRate) }
      : {}),
    ...(requiredTool ? { requiredTool } : {}),
    ...(seasonalMultiplier ? { seasonalMultiplier } : {}),
  };
};

export const normalizePosition = (value: unknown, layout: WorldLayout): AnnotationTilePoint => {
  const entry = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    x: clampTile(entry.x, layout.tileWidth - 1),
    y: clampTile(entry.y, layout.tileHeight - 1),
  };
};

export const normalizeSize = (value: unknown, layout: WorldLayout): AnnotationSize | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  return {
    width: Math.max(1, Math.min(layout.tileWidth, Math.floor(toFiniteNumber(entry.width, 1)))),
    height: Math.max(1, Math.min(layout.tileHeight, Math.floor(toFiniteNumber(entry.height, 1)))),
  };
};

export const normalizeDoorTarget = (
  value: unknown,
  layout: WorldLayout,
): AnnotationDoorTarget | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const zone = sanitizeString(entry.zone);
  return {
    tileX: clampTile(entry.tileX, layout.tileWidth - 1),
    tileY: clampTile(entry.tileY, layout.tileHeight - 1),
    zone: zone || 'default',
  };
};

export const clampRectToLayout = (
  position: AnnotationTilePoint,
  size: AnnotationSize,
  layout: WorldLayout,
): { position: AnnotationTilePoint; size: AnnotationSize } => {
  const maxWidth = Math.max(1, layout.tileWidth - position.x);
  const maxHeight = Math.max(1, layout.tileHeight - position.y);
  return {
    position,
    size: {
      width: Math.max(1, Math.min(size.width, maxWidth)),
      height: Math.max(1, Math.min(size.height, maxHeight)),
    },
  };
};

export const normalizeLegacyType = (value: unknown): 'sign' | 'door' | undefined =>
  value === 'sign' || value === 'door' ? value : undefined;

export const normalizeAnnotationRecord = (
  annotation: WorldAnnotation,
  layout: WorldLayout,
): WorldAnnotation | null => {
  const id = sanitizeString(annotation.id);
  if (!id) {
    return null;
  }

  const shape = normalizeShape(annotation.shape);
  const position = normalizePosition(annotation.position, layout);
  const normalizedAnnotation: WorldAnnotation = {
    id,
    name: sanitizeString(annotation.name) || 'Untitled Annotation',
    description: sanitizeString(annotation.description),
    category: normalizeCategory(annotation.category),
    shape,
    position,
  };
  const normalizedTags = normalizeTags(annotation.tags);
  if (normalizedTags) {
    normalizedAnnotation.tags = normalizedTags;
  }
  if (typeof annotation.playerVisible === 'boolean') {
    normalizedAnnotation.playerVisible = annotation.playerVisible;
  }
  const normalizedExamineText = sanitizeString(annotation.examineText);
  if (normalizedExamineText) {
    normalizedAnnotation.examineText = normalizedExamineText;
  }
  if (Number.isFinite(annotation.interactionRadius)) {
    normalizedAnnotation.interactionRadius = Math.max(
      1,
      Math.floor(toFiniteNumber(annotation.interactionRadius, DEFAULT_INTERACTION_RADIUS)),
    );
  }
  const normalizedInteractions = normalizeAnnotationInteractions(annotation.interactions);
  if (normalizedInteractions) {
    normalizedAnnotation.interactions = normalizedInteractions;
  }
  const normalizedTriggers = normalizeAnnotationTriggers(annotation.triggers);
  if (normalizedTriggers) {
    normalizedAnnotation.triggers = normalizedTriggers;
  }
  const normalizedState = normalizeAnnotationStateRecord(annotation.state);
  if (normalizedState) {
    normalizedAnnotation.state = normalizedState;
  }
  const normalizedFunctionValue = normalizeFunction(annotation.function);
  if (normalizedFunctionValue) {
    normalizedAnnotation.function = normalizedFunctionValue;
  }
  const normalizedCapacity = normalizeCapacity(annotation.capacity);
  if (normalizedCapacity) {
    normalizedAnnotation.capacity = normalizedCapacity;
  }
  const normalizedProductionConfig = normalizeProductionConfig(annotation.productionConfig);
  if (normalizedProductionConfig) {
    normalizedAnnotation.productionConfig = normalizedProductionConfig;
  }
  const normalizedResourceNodeConfig = normalizeResourceNodeConfig(annotation.resourceNodeConfig);
  if (normalizedResourceNodeConfig) {
    normalizedAnnotation.resourceNodeConfig = normalizedResourceNodeConfig;
  }
  const normalizedCreatedBy = sanitizeString(annotation.createdBy);
  if (normalizedCreatedBy) {
    normalizedAnnotation.createdBy = normalizedCreatedBy;
  }
  if (Number.isFinite(annotation.createdAt)) {
    normalizedAnnotation.createdAt = Math.max(
      0,
      Math.floor(toFiniteNumber(annotation.createdAt, 0)),
    );
  }
  const normalizedLegacyType = normalizeLegacyType(annotation.legacyType);
  if (normalizedLegacyType) {
    normalizedAnnotation.legacyType = normalizedLegacyType;
  }
  const normalizedDoorTarget = normalizeDoorTarget(annotation.doorTarget, layout);
  if (normalizedDoorTarget) {
    normalizedAnnotation.doorTarget = normalizedDoorTarget;
  }

  if (shape === 'rect') {
    const size = normalizeSize(annotation.size, layout);
    if (!size) {
      return null;
    }
    const rect = clampRectToLayout(position, size, layout);
    return {
      ...normalizedAnnotation,
      position: rect.position,
      size: rect.size,
    };
  }

  if (shape === 'circle') {
    return {
      ...normalizedAnnotation,
      radius: Math.max(1, Math.floor(toFiniteNumber(annotation.radius, 1))),
    };
  }

  return normalizedAnnotation;
};
